import { useState, useEffect } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const careersJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '픽스코프 채용 | Fixcope Careers',
  url: `${siteUrl}/careers`,
  description: '픽스코프와 함께 성장할 인재를 찾습니다. 엔지니어링, 디자인, 영업, 마케팅 등 다양한 포지션에 지원하세요.',
  inLanguage: 'ko-KR',
  mainEntity: {
    '@type': 'Organization',
    name: 'Fixcope',
    url: siteUrl,
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Fixcope',
    },
  },
};

export default function CareersPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const benefits = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs for you and your family.'
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Work',
      description: 'Hybrid work model with flexible hours and remote work options to support work-life balance.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Learning & Development',
      description: 'Continuous learning opportunities, certifications, conferences, and professional development programs.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Compensation',
      description: 'Market-leading salaries, performance bonuses, equity options, and comprehensive benefits package.'
    },
    {
      icon: 'ri-plane-line',
      title: 'Paid Time Off',
      description: 'Generous vacation policy, paid holidays, parental leave, and sabbatical opportunities.'
    },
    {
      icon: 'ri-team-line',
      title: 'Team Culture',
      description: 'Collaborative environment, team events, diversity initiatives, and inclusive workplace culture.'
    }
  ];

  const departments = ['All', 'Engineering', 'Design', 'Product', 'Sales', 'Marketing', 'Operations'];

  const positions = [
    {
      title: 'Senior Full Stack Engineer',
      department: 'Engineering',
      location: 'San Francisco, CA / Remote',
      type: 'Full-time',
      description: 'Build scalable web applications using modern technologies. Work with React, Node.js, and cloud infrastructure to deliver exceptional user experiences.',
      requirements: ['5+ years of full-stack development experience', 'Expertise in React, TypeScript, and Node.js', 'Experience with cloud platforms (AWS/Azure/GCP)', 'Strong problem-solving and communication skills']
    },
    {
      title: 'Cloud Solutions Architect',
      department: 'Engineering',
      location: 'New York, NY / Hybrid',
      type: 'Full-time',
      description: 'Design and implement enterprise cloud solutions. Lead cloud migration projects and architect scalable, secure infrastructure for our clients.',
      requirements: ['7+ years of cloud architecture experience', 'AWS/Azure/GCP certifications', 'Experience with microservices and containerization', 'Strong leadership and client-facing skills']
    },
    {
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create beautiful, intuitive user experiences for web and mobile applications. Collaborate with product and engineering teams to bring designs to life.',
      requirements: ['4+ years of UX/UI design experience', 'Proficiency in Figma, Sketch, or Adobe XD', 'Strong portfolio demonstrating design thinking', 'Experience with design systems and accessibility']
    },
    {
      title: 'Product Manager',
      department: 'Product',
      location: 'San Francisco, CA / Hybrid',
      type: 'Full-time',
      description: 'Drive product strategy and roadmap for our enterprise solutions. Work cross-functionally to deliver products that delight customers and drive business growth.',
      requirements: ['5+ years of product management experience', 'Experience with B2B SaaS products', 'Strong analytical and strategic thinking skills', 'Excellent stakeholder management abilities']
    },
    {
      title: 'DevOps Engineer',
      department: 'Engineering',
      location: 'Austin, TX / Remote',
      type: 'Full-time',
      description: 'Build and maintain CI/CD pipelines, automate infrastructure, and ensure system reliability. Work with cutting-edge DevOps tools and practices.',
      requirements: ['4+ years of DevOps experience', 'Expertise in Kubernetes, Docker, and Terraform', 'Experience with monitoring and observability tools', 'Strong scripting skills (Python, Bash, etc.)']
    },
    {
      title: 'Enterprise Sales Executive',
      department: 'Sales',
      location: 'New York, NY / Hybrid',
      type: 'Full-time',
      description: 'Drive revenue growth by building relationships with enterprise clients. Identify opportunities and close complex deals in the technology consulting space.',
      requirements: ['6+ years of enterprise sales experience', 'Proven track record of exceeding quotas', 'Experience selling technology solutions', 'Strong negotiation and presentation skills']
    },
    {
      title: 'Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead marketing campaigns and brand initiatives. Develop strategies to increase brand awareness and generate qualified leads for our sales team.',
      requirements: ['5+ years of B2B marketing experience', 'Experience with digital marketing and content strategy', 'Strong analytical and creative skills', 'Excellent written and verbal communication']
    },
    {
      title: 'Data Scientist',
      department: 'Engineering',
      location: 'Boston, MA / Hybrid',
      type: 'Full-time',
      description: 'Apply machine learning and statistical analysis to solve complex business problems. Work with large datasets to derive actionable insights.',
      requirements: ['4+ years of data science experience', 'Expertise in Python, R, and ML frameworks', 'Strong statistical and mathematical background', 'Experience with big data technologies']
    },
    {
      title: 'Operations Coordinator',
      department: 'Operations',
      location: 'Chicago, IL / Hybrid',
      type: 'Full-time',
      description: 'Support day-to-day operations and ensure smooth execution of projects. Coordinate across teams to optimize processes and improve efficiency.',
      requirements: ['3+ years of operations experience', 'Strong organizational and project management skills', 'Proficiency in operations tools and software', 'Excellent communication and problem-solving abilities']
    }
  ];

  const filteredPositions = selectedDepartment === 'All' 
    ? positions 
    : positions.filter(pos => pos.department === selectedDepartment);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="픽스코프 채용 | Fixcope - 함께 성장할 인재를 찾습니다"
        description="픽스코프와 함께 성장할 인재를 찾습니다. 엔지니어링, 디자인, 제품, 영업, 마케팅 등 다양한 포지션에 지원하고 글로벌 기술 솔루션 기업의 일원이 되세요."
        keywords="픽스코프 채용, Fixcope careers, 취업, 개발자 채용, 엔지니어 채용, 기술 기업 취업"
        canonical="/careers"
        ogTitle="픽스코프 채용 | Fixcope"
        ogDescription="픽스코프와 함께 성장할 인재를 찾습니다. 다양한 포지션에 지원하세요."
        lang="ko"
        jsonLd={careersJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0066CC] via-[#0052A3] to-[#003D7A]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Build your career with a team that values innovation, collaboration, and continuous growth. Help us shape the future of technology consulting.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 bg-gradient-to-b from-[#F8FBFF] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/dcc1434ecc53d95fdbba33526ff59c3c.jpg"
                  alt="Our Culture"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-[#0066CC] to-[#003D7A] rounded-2xl opacity-20"></div>
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-[#E6F2FF] rounded-full mb-6">
                <span className="text-sm font-semibold text-[#0066CC]">OUR CULTURE</span>
              </div>
              <h2 className="text-4xl font-bold text-[#1A2332] mb-6">
                Where Innovation Meets Collaboration
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At TechVision, we foster a culture of innovation, continuous learning, and mutual respect. Our team members are empowered to take ownership, experiment with new ideas, and make meaningful contributions to our clients' success.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe in creating an inclusive environment where diverse perspectives are valued, work-life balance is prioritized, and every team member has the opportunity to grow both personally and professionally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#E6F2FF] rounded-full mb-6">
              <span className="text-sm font-semibold text-[#0066CC]">BENEFITS</span>
            </div>
            <h2 className="text-4xl font-bold text-[#1A2332] mb-4">
              Why Work With Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer comprehensive benefits and perks designed to support your well-being and career growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-[#F8FBFF] to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#0066CC] to-[#003D7A] rounded-xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-4">{benefit.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gradient-to-b from-[#F8FBFF] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-[#E6F2FF] rounded-full mb-6">
              <span className="text-sm font-semibold text-[#0066CC]">OPEN POSITIONS</span>
            </div>
            <h2 className="text-4xl font-bold text-[#1A2332] mb-4">
              Current Opportunities
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our open positions and find the perfect role to advance your career.
            </p>
          </div>

          {/* Department Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedDepartment === dept
                    ? 'bg-gradient-to-r from-[#0066CC] to-[#003D7A] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Positions List */}
          <div className="space-y-6">
            {filteredPositions.map((position, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="px-4 py-1 bg-[#E6F2FF] text-[#0066CC] rounded-full text-sm font-semibold">
                        {position.department}
                      </span>
                      <span className="px-4 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                        {position.type}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#1A2332] mb-3">{position.title}</h3>
                    <div className="flex items-center text-gray-600 mb-4">
                      <i className="ri-map-pin-line mr-2"></i>
                      <span className="text-base">{position.location}</span>
                    </div>
                    <p className="text-base text-gray-600 leading-relaxed mb-4">{position.description}</p>
                    <div className="space-y-2">
                      <div className="font-semibold text-[#1A2332] text-base">Key Requirements:</div>
                      <ul className="space-y-2">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start text-base text-gray-600">
                            <i className="ri-checkbox-circle-fill text-[#0066CC] mr-2 mt-1 flex-shrink-0"></i>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <button className="px-8 py-4 bg-gradient-to-r from-[#0066CC] to-[#003D7A] text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredPositions.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-lg text-gray-500">No positions available in this department at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0066CC] via-[#0052A3] to-[#003D7A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            We're always looking for talented individuals to join our team. Send us your resume and let's explore opportunities together.
          </p>
          <button className="px-8 py-4 bg-white text-[#0066CC] rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-300 shadow-lg whitespace-nowrap cursor-pointer">
            Submit Your Resume
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
